// ============================================================================
// backgroundActivity — a job the user started, still reporting in
// ============================================================================
//
// SESSION-LOGOUT-WHILE-POLLING-01 / H1.
//
// The idle timer (hooks/useIdleTimer.ts) counts DOM input events and nothing
// else: mousemove, mousedown, keydown, touchstart, scroll, click. That is the
// right definition of "present" for almost every screen in the app -- and
// exactly the wrong one for the QTO cockpit, where the user's whole job is to
// WATCH. A multi-hour take-off updates itself every 2 s; there is no reason to
// touch the mouse, and after 15 minutes of not touching it the Master was
// signed out of a session he was actively using.
//
// HTTP traffic on its own is not presence -- a forgotten tab polls just as
// eagerly as a watched one, and treating any request as activity would disable
// the idle timeout for anyone who left the app open. So the signal is narrower,
// and it takes three things at once:
//
//   1. the request is marked `background: true` (lib/api.ts), which today means
//      the cockpit's poll of a job THIS USER enqueued,
//   2. it SUCCEEDED -- a 200 is the job saying it is still running,
//   3. the tab is VISIBLE (checked by the subscriber, not here).
//
// Terminality needs no check: the poll stops scheduling ticks the moment the
// job reaches completed/failed/cancelled (QtoCockpitBridgeCta's tick returns
// without re-arming), so the pings simply stop and the normal 15-minute rule
// takes over from the last one. The timeout stays a security control; it is
// suspended only while a job the user started is demonstrably alive in front of
// them.
//
// Dependency-free (no React, no '@/lib/api') so lib/api.ts can publish into it
// without a cycle -- the same shape as lib/authTokenSync.
// ============================================================================

type Listener = () => void

const listeners = new Set<Listener>()

/**
 * A `background: true` request just came back 2xx.
 *
 * Fire-and-forget: never throws, so a subscriber's bug cannot turn into a
 * failed API call for the caller that published.
 */
export function publishBackgroundActivity(): void {
  for (const listener of Array.from(listeners)) {
    try {
      listener()
    } catch {
      /* a throwing subscriber is its own problem */
    }
  }
}

export function subscribeBackgroundActivity(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Test-only: drop every subscriber between cases. */
export function __resetBackgroundActivityForTests(): void {
  listeners.clear()
}
